import { Flex, Pagination, Table, TableProps } from 'antd';
import { TextComponent } from 'baseComponents/TextComponent';
import { Key, ReactNode, useState } from 'react';
import './table.scss';
import { TableRowSelection } from 'antd/es/table/interface';

type RequiredTableKeys = 'columns' | 'dataSource';

type TableComponentProps<Item extends object> = Omit<
  TableProps<Item>,
  RequiredTableKeys
> &
  Required<Pick<TableProps<Item>, RequiredTableKeys>> & {
    tableName?: string;
    ctaButtons?: ReactNode;
    pageSize?: number;
    initialPage?: number;
    total: number;
    selectable?: boolean;
    selectionType?: 'checkbox' | 'radio';
    onSelection?: (selectedRowKeys: Key[], selectedRows: Item[]) => void;
    width?: number;
    tableClassname?: string;
  };

export const TableComponent = <Item extends object>({
  tableName,
  ctaButtons,
  pageSize = 20,
  initialPage = 1,
  total,
  onSelection,
  selectable = false,
  width,
  tableClassname,
  selectionType = 'radio',
  ...tableProps
}: TableComponentProps<Item>) => {
  const [activePage, setActivePage] = useState(initialPage);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  const rowSelection: TableRowSelection<Item> = {
    type: selectionType,
    selectedRowKeys,
    hideSelectAll: true,
    onChange: (selectedRowKeys: Key[], selectedRows: Item[]) => {
      setSelectedRowKeys(selectedRowKeys);
      onSelection?.(selectedRowKeys, selectedRows);
    },
    getCheckboxProps: (record) => ({
      // @ts-ignore
      disabled: record?.selectable ?? false, // Column configuration not to be checked
    }),
  };

  const paginationProps = {
    pageSize,
    current: activePage,
    total,
  };

  return (
    <Flex vertical gap={20}>
      {tableName && <TextComponent size='table-title-size' text={tableName} />}
      <Flex vertical gap={16}>
        <Table
          {...tableProps}
          className={`pj-table ${tableClassname ? tableClassname : ''}`}
          rowSelection={selectable ? rowSelection : undefined}
          pagination={{
            position: ['none'],
            ...paginationProps,
          }}
          scroll={{ x: width }}
        />
        <Flex justify='space-between' align='center' gap={16}>
          <Pagination
            onChange={(page) => setActivePage(page)}
            className='pj-table-pagination'
            showSizeChanger={false}
            showLessItems
            {...paginationProps}
          />
          {ctaButtons}
        </Flex>
      </Flex>
    </Flex>
  );
};
